'use client';

import { Icon } from '@/components/ui';
import { BRAND } from '@/lib/cms/config';
import { MANUFACTURED_BY_LABEL_KEYS } from '@/lib/product/manufacturedByLabelKeys';
import type { KeyFeature } from '@/lib/product/types';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { Link } from '@/lib/i18n/navigation';
import { useState } from 'react';
import styles from './KeyFeatures.module.scss';

// Mapping from feature labels to translation keys
const featureLabelKeys: Record<string, string> = {
  'Case material': 'technicalData.case.caseMaterial',
  'Water resistance': 'technicalData.case.waterResistance',
  Diameter: 'technicalData.dimensions.diameter',
  Thickness: 'technicalData.dimensions.thickness',
  'Product weight (approx.)': 'technicalData.dimensions.productWeight',
  'Power reserve': 'technicalData.movement.powerReserve',
  Caliber: 'technicalData.movement.caliber',
  Movement: 'technicalData.movement.movement',
  // Shown in "Main features" on both the watch PDP (BEC-24058, replaces weight)
  // and My Watches; value is a proper noun. Also accepts the CMS/Saleor slug.
  ...MANUFACTURED_BY_LABEL_KEYS,
};

export interface KeyFeaturesProps {
  features: KeyFeature[];
  heading?: string;
  manualUrl?: string;
  /**
   * Show the "SWISS MADE" tag next to the manual link. Defaults to true (PDP
   * behavior); the My Watches detail page opts out — it's not in that Figma.
   */
  showSwissMade?: boolean;
  className?: string;
}

/**
 * Format a value to remove unnecessary decimal places
 * e.g., "32.0" → "32", "32.5" → "32.5"
 */
function formatValue(value: string): string {
  return value.replace(/(\d+)\.0(\D|$)/g, '$1$2');
}

/**
 * Parse heading into accent and rest parts for Breitling split-color styling
 * "KEY FEATURES" → "KEY" (accent) + "FEATURES" (rest)
 */
function parseHeading(heading: string): { accent: string; rest: string } {
  const words = heading.split(' ');
  if (words.length >= 2) {
    return { accent: words[0], rest: words.slice(1).join(' ') };
  }
  return { accent: '', rest: heading };
}

/**
 * Key features section showing main product specifications with icons
 * Based on Breitling's KeyFeaturesSection pattern
 */
export function KeyFeatures({
  features,
  heading,
  manualUrl,
  showSwissMade = true,
  className,
}: Readonly<KeyFeaturesProps>) {
  const t = useTranslations('product');
  const displayHeading =
    heading ?? (BRAND === 'gallet' ? t('keyFeatures.mainFeatures') : t('keyFeatures.title'));
  // Only parse for split-color on Breitling
  const { accent, rest } =
    BRAND === 'breitling' ? parseHeading(displayHeading) : { accent: '', rest: displayHeading };

  // Helper to translate feature label
  const translateLabel = (label: string): string => {
    const key = featureLabelKeys[label];
    if (key) {
      const translated = t(key);
      if (translated && translated !== key) {
        return translated;
      }
    }
    return label; // Fallback to original
  };

  return (
    <section className={clsx(styles.section, className)}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.heading}>
            {accent && <span className={styles.headingAccent}>{accent}</span>}
            {rest && ` ${rest}`}
          </h2>
          <div className={styles.headerRight}>
            {manualUrl && showSwissMade && (
              <span className={styles.swissMade}>{t('swissMade')}</span>
            )}
            {manualUrl && (
              <Link
                href={manualUrl}
                className={styles.download}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>{t('downloadUserManual')}</span>
                <Icon name="download" size={16} />
              </Link>
            )}
          </div>
        </div>

        {/* Features Grid */}
        <div
          className={styles.grid}
          style={
            {
              '--feature-columns': Math.min(Math.max(features.length, 1), 6),
            } as React.CSSProperties
          }
        >
          {features.map((feature) => (
            <FeatureItem key={feature.id} feature={feature} translateLabel={translateLabel} t={t} />
          ))}
        </div>

        {/* Mobile-only download link */}
        {manualUrl && (
          <Link
            href={manualUrl}
            className={styles.downloadMobile}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>{t('downloadUserManual')}</span>
            <Icon name="download" size={16} />
          </Link>
        )}
      </div>
    </section>
  );
}

// Figma-exported SVG icon paths mapped by icon type
// Icons are organized by brand for proper theming
const FEATURE_ICON_NAMES: Record<string, string> = {
  case: 'case_material.svg',
  water: 'water_resistance.svg',
  diameter: 'diameter.svg',
  thickness: 'thickness.svg',
  weight: 'weight.svg',
  power: 'power_reserve.svg',
  // "Manufactured by" (gold Breitling-B) — on the watch PDP and My Watches.
  manufactured: 'manufactured.svg',
  // Strap PDP main features.
  'strap-material': 'strap_material.png',
  'lug-width': 'lug_width.png',
  'buckle-type': 'buckle_type.png',
  'lug-type': 'lug_type.png',
};

/**
 * Get the icon path for a feature, using brand-specific icons
 */
function getFeatureIconPath(icon: string): string | null {
  const iconName = FEATURE_ICON_NAMES[icon];
  if (!iconName) return null;
  return `/icons/features/${BRAND}/${iconName}`;
}

/**
 * Individual feature item with optional tooltip
 */
function FeatureItem({
  feature,
  translateLabel,
  t: _t,
}: Readonly<{
  feature: KeyFeature;
  translateLabel: (label: string) => string;
  t: (key: string) => string;
}>) {
  const [showTooltip, setShowTooltip] = useState(false);
  const tCommon = useTranslations('common');

  return (
    <div className={styles.feature}>
      <div className={styles.iconWrapper}>
        <FeatureIcon icon={feature.icon} swatchColor={feature.swatchColor} />
      </div>
      <div className={styles.content}>
        <span className={styles.label}>
          {translateLabel(feature.label)}
          {feature.tooltip && (
            <button
              type="button"
              className={styles.infoButton}
              onClick={() => setShowTooltip(!showTooltip)}
              aria-label={tCommon('moreInformation')}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5" />
                <path d="M7 6V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="7" cy="4" r="0.75" fill="currentColor" />
              </svg>
            </button>
          )}
        </span>
        <span className={styles.value}>{formatValue(feature.value)}</span>
        {feature.tooltip && showTooltip && <div className={styles.tooltip}>{feature.tooltip}</div>}
      </div>
    </div>
  );
}

function FeatureIcon({ icon, swatchColor }: Readonly<{ icon?: string; swatchColor?: string }>) {
  // Colour is shown as a filled swatch rather than an illustration. Without a
  // colour from the PIM there is nothing meaningful to draw, so draw nothing —
  // a wrong swatch is worse than none on a product page.
  if (icon === 'color') {
    if (!swatchColor) return null;
    return (
      <span className={styles.swatch} style={{ backgroundColor: swatchColor }} aria-hidden="true" />
    );
  }

  const iconSrc = icon ? getFeatureIconPath(icon) : null;

  if (iconSrc) {
    const iconSize = BRAND === 'breitling' ? 70 : 40;
    return <img src={iconSrc} alt="" className={styles.icon} width={iconSize} height={iconSize} />;
  }

  // Fallback to inline SVG for unknown icons
  return (
    <svg viewBox="0 0 70 70" fill="none" className={styles.icon}>
      <circle cx="35" cy="35" r="34" stroke="var(--color-orange)" strokeWidth="1" fill="none" />
      <circle cx="35" cy="35" r="15" stroke="var(--color-orange)" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

KeyFeatures.displayName = 'KeyFeatures';
